/**
 * "Connect with Stripe" on the wallet — the REAL flow behind Blake's button
 * (card 1217981132152628).
 *
 * 🔴 #wal-connect was GUARDED-HONEST, not dead. Blake's own handler
 * (app.js:4671) fabricates a completed onboarding: 1400ms of walletVerifying,
 * then `WALLET.bank.connected = true` and a toast claiming
 * "<institution> ···· <last4> verified · payouts on" — an assertion about an
 * identity check that never ran. writeGuard.ts suppressed the click for
 * exactly that reason, so the button did nothing, which is how Tyler met it on
 * staging 2026-08-30 ("that button goes nowhere") minutes after finishing a
 * registration whose decline copy promises "You can connect Stripe anytime
 * from your Profile." The signup made a promise the profile did not keep.
 * This module keeps it.
 *
 * THE FLOW IS register.tsx's, SAME SHAPE, SAME TRAPS PAID FOR:
 *
 *   GET  /api/users/me                     → the email the server REQUIRES
 *        (POST /connect/accounts 400s without one — fetched fresh here rather
 *        than read off ACCOUNT, so this works even if installAccount's lazy
 *        boot has not run or failed)
 *   POST /api/stripe/connect/accounts      { email, country: "US",
 *        businessType: "individual" }      → { accountId }
 *   POST /api/stripe/connect/accounts/:id/onboarding-link
 *        { returnUrl:  <origin>/stripe-connect-return?ctx=web,
 *          refreshUrl: <origin>/stripe-connect-refresh?ctx=web } → { url }
 *   win.location.assign(url)
 *
 * 🪤 `?ctx=web` IS LOAD-BEARING. Both return pages default to the APP path and
 * fire `gobazaar://stripe-connect-return` on mount; without the marker a
 * browser with no app installed lands on the 2026-08-01 dead end ("You're all
 * set!" over Safari's "address is invalid"). Absent means app.
 *
 * 🪤 CAPTURE + stopPropagation — the writeGuard mechanics exactly. Blake binds
 * his fabrication in the target phase on the button itself; a capture listener
 * on document that stops propagation means his handler never fires,
 * registration order irrelevant. stopPropagation and NOT
 * stopImmediatePropagation, per writeGuard's own note — this page runs other
 * capture listeners on document (linkFixups, the guard itself) that must keep
 * seeing events; they filter by selector and pass this one by, since
 * #wal-connect is no longer in the guard's list.
 *
 * SERVER CONTRACT, checked not assumed: POST /connect/accounts ALWAYS creates
 * a fresh Stripe account and overwrites users.stripe_account_id (connect.ts —
 * `.set({ stripeAccountId: account.id, stripeConnected: false })`). A re-click
 * after an abandoned onboarding therefore starts over with a new account,
 * exactly as the app's wallet does; the orphaned account is Stripe-side noise
 * the server already accepts. Not this module's contract to improve.
 *
 * FAILURE IS HONEST AND RETRYABLE: any non-ok step re-enables the button and
 * toasts a plain sentence. No fabricated progress, no fake verifying state —
 * Blake's `walletVerifying` variable is top-level in a classic script and
 * unreachable from ES modules anyway, which is its own reminder that the only
 * states this module may paint are the ones it can make true.
 */

type AuthFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** listingVotes' toast idiom: Blake's global, guarded on his #toast node —
 *  degrade to silence rather than throw before the first render. */
function say(win: Window, message: string): void {
  if (!win.document.getElementById("toast")) return;
  (win as unknown as { toast?: (m: string) => void }).toast?.(message);
}

const FAIL_MSG = "Could not start Stripe setup. Please try again in a moment.";

/** Returns an uninstall, so tests can unbind between cases. The boot path may
 *  ignore it — the listener is meant to live as long as the tab. */
export function installStripeConnectLaunch(win: Window, authFetch: AuthFetch): () => void {
  /* In the closure, not on the element: Blake's rerenders replace the button
     node, so a flag stored there would reset mid-flight. */
  let inFlight = false;

  const onClick = (e: Event) => {
    const target = e.target as Element | null;
    const btn = target?.closest?.("#wal-connect");
    if (!btn) return;
    // Kill the descent so Blake's target-phase fabrication never runs.
    e.stopPropagation();
    e.preventDefault();
    if (inFlight) return;
    inFlight = true;
    btn.setAttribute("disabled", "");

    void (async () => {
      try {
        const meRes = await authFetch("/api/users/me");
        if (!meRes.ok) throw new Error("me " + meRes.status);
        const me = (await meRes.json()) as { email?: string | null };
        if (!me.email) throw new Error("no email on file");

        const createRes = await authFetch("/api/stripe/connect/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: me.email, country: "US", businessType: "individual" }),
        });
        if (!createRes.ok) throw new Error("create " + createRes.status);
        const { accountId } = (await createRes.json()) as { accountId?: string };
        if (!accountId) throw new Error("no accountId");

        const origin = win.location.origin;
        const linkRes = await authFetch(
          `/api/stripe/connect/accounts/${encodeURIComponent(accountId)}/onboarding-link`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              returnUrl: `${origin}/stripe-connect-return?ctx=web`,
              refreshUrl: `${origin}/stripe-connect-refresh?ctx=web`,
            }),
          },
        );
        if (!linkRes.ok) throw new Error("link " + linkRes.status);
        const { url } = (await linkRes.json()) as { url?: string };
        if (!url) throw new Error("no url");

        /* inFlight stays true on purpose: this document is leaving. If the
           visitor backs out of Stripe, the page reloads and this closure is
           rebuilt fresh. */
        win.location.assign(url);
      } catch {
        inFlight = false;
        btn.removeAttribute("disabled");
        say(win, FAIL_MSG);
      }
    })();
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
